import AdvancedTooltip from '@/components/common/AdvancedTooltip';
import Tooltip from '@/components/common/Tooltip';
import { PortalOptionPositionRow } from '@/components/portal-modules/PortalOptionPositionTable';
import { convertedQtyFormatterConfig } from '@/config/config';
import { largeCell, mediumCell, smallCell } from '@/constants/table';
import { CurrenciesResponse, Currency, FxOptionPositionData } from '@/types/sso';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { BigNumber } from 'bignumber.js';
import cn from 'classnames';
import { useNumberFormatter } from 'react-aria';
import { MdInfoOutline } from 'react-icons/md';
import { generalAmountCell } from './currencyHelper';

export const portalOptionPositionColumns = (
    baseCurrency: string,
    accountType,
    totalInBaseCurrency: number,
    instrumentConfigs
) => {
    const columns: ColumnDef<PortalOptionPositionRow>[] = [
        {
            accessorKey: 'tableSymbol',
            header: ({ table }) => {
                return (
                    <div className={`flex gap-1 items-center`}>
                        <span>Crypto Option</span>
                    </div>
                );
            },
            cell: ({ row, getValue }: CellContext<PortalOptionPositionRow, any>) => {
                const cellValue = getValue();
                const selectedRow = row.original;

                const precision = getCurrency(row.original.premiumCurrencyName)?.order_decimals;

                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return (
                    <div className={`flex gap-1`}>
                        <div className="flex items-center gap-0.5">
                            <AdvancedTooltip
                                content={
                                    <div className="flex flex-col space-y-2">
                                        <div>Option Position Details</div>
                                        <div className="px-1 space-y-1">
                                            <TooltipEntry label="Status" value={selectedRow.status} />
                                            <TooltipEntry label="Trade Date" value={selectedRow.tableTradeDate} />
                                            <TooltipEntry label="Expiry Date" value={selectedRow.tableExpiryDate} />
                                            <TooltipEntry label="Style" value={selectedRow.style} />
                                            <TooltipEntry label="Type" value={selectedRow.type} />
                                            <TooltipEntry label="Variant" value={selectedRow.variant} />
                                            {(selectedRow.lowerBarrier || selectedRow.upperBarrier) && (
                                                <TooltipEntry
                                                    label="Lower Barrier"
                                                    value={generalAmountCell({
                                                        amount: selectedRow.lowerBarrier,
                                                        ccy: selectedRow.quoteAssetName,
                                                        formatQty
                                                    })}
                                                />
                                            )}
                                            {(selectedRow.lowerBarrier || selectedRow.upperBarrier) && (
                                                <TooltipEntry
                                                    label="Upper Barrier"
                                                    value={generalAmountCell({
                                                        amount: selectedRow.upperBarrier,
                                                        ccy: selectedRow.quoteAssetName,
                                                        formatQty
                                                    })}
                                                />
                                            )}
                                        </div>
                                        <div>End of Details</div>
                                    </div>
                                }
                                className="!text-brand-neutral-200 !bg-brand-background-dark border border-brand-neutral-700 font-normal"
                                placement="bottom-start">
                                <div className={`flex items-center space-x-1`}>
                                    <span className="">{cellValue}</span>
                                    <MdInfoOutline className="w-3.5 h-3.5" name="Option Info" />
                                </div>
                            </AdvancedTooltip>
                        </div>
                    </div>
                );
            },
            footer: () => <span className="p-2 text-sm font-semibold">Total ({baseCurrency}) :</span>
        },
        {
            ...mediumCell,
            accessorKey: 'strikePrice',
            header: 'Strike Price',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => {
                const amount = info.getValue();
                const ccy = info.row.original.quoteAssetName;
                const underlyingInstrument = info.row.original.instrumentName;
                const precision = getInstrumentConfig(
                    accountType,
                    instrumentConfigs,
                    underlyingInstrument
                )?.priceDecimals;

                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return generalAmountCell({
                    amount,
                    ccy,
                    formatQty
                });
            },
            footer: () => {}
        },
        {
            ...mediumCell,
            accessorKey: 'tableAmount',
            header: 'Amount',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => info.getValue(),
            footer: () => {}
        },
        {
            ...mediumCell,
            accessorKey: 'premiumPrice',
            header: 'Price',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => {
                const amount = info.getValue();
                const ccy = info.row.original.premiumCurrencyName;
                const precision = getCurrency(ccy)?.order_decimals;
                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return generalAmountCell({
                    amount,
                    ccy,
                    formatQty
                });
            },
            footer: () => {}
        },
        {
            ...largeCell,
            accessorKey: 'premium',
            header: 'Premium',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => {
                const amount = info.getValue();
                const ccy = info.row.original.premiumCurrencyName;
                const precision = getCurrency(ccy)?.order_decimals;
                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return generalAmountCell({
                    amount,
                    ccy,
                    formatQty
                });
            },
            footer: () => {}
        },
        {
            ...mediumCell,
            accessorKey: 'currentPrice',
            header: 'Curr. Price',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => {
                const original = info.row.original;
                const isPriorBusinessDay = original.currentPriceStatus === 'PRIOR_BUSINESS_DAY';
                const isFallback = original.currentPriceStatus === 'FALLBACK';

                let tooltip;
                if (isPriorBusinessDay) tooltip = 'Price as of prior business day';
                if (isFallback) tooltip = 'Unable to determine current price, using fallback price';

                const amount = info.getValue();
                const ccy = info.row.original.premiumCurrencyName;
                const precision = getCurrency(ccy)?.order_decimals;
                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return (
                    <Tooltip content={tooltip}>
                        {generalAmountCell({
                            amount,
                            ccy,
                            formatQty,
                            className: cn({
                                '!text-yellow-500': isPriorBusinessDay,
                                '!text-brand-red': isFallback
                            })
                        })}
                    </Tooltip>
                );
            },
            footer: () => {}
        },
        {
            ...mediumCell,
            accessorKey: 'currentValue',
            header: 'Curr. Value',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => {
                const amount = info.getValue();
                const ccy = info.row.original.premiumCurrencyName;
                const precision = getCurrency(ccy)?.order_decimals;
                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return generalAmountCell({
                    amount,
                    ccy,
                    formatQty
                });
            },
            footer: () => {}
        },
        {
            ...mediumCell,
            accessorKey: 'currentValueInBaseCurrency',
            header: `Curr. Value (${baseCurrency})`,
            cell: (info: CellContext<PortalOptionPositionRow, any>) => {
                const amount = info.getValue();

                const precision = getCurrency(baseCurrency)?.order_decimals;
                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return generalAmountCell({
                    amount,
                    ccy: baseCurrency,
                    formatQty
                });
            },
            footer: (row) => {
                const precision = getCurrency(baseCurrency)?.order_decimals;
                const formatQty = useNumberFormatter({
                    minimumFractionDigits: precision > 0 ? precision : 0,
                    maximumFractionDigits: precision > 0 ? precision : 0,
                    trailingZeroDisplay: 'stripIfInteger',
                    roundingMode: 'halfEven'
                });

                return (
                    <span className="p-2 text-sm font-normal">
                        {generalAmountCell({
                            amount: totalInBaseCurrency,
                            ccy: baseCurrency,
                            formatQty
                        })}
                    </span>
                );
            }
        },
        {
            ...smallCell,
            accessorKey: 'tableExpiryDate',
            header: 'Expiry Date',
            cell: (info: CellContext<PortalOptionPositionRow, any>) => info.getValue(),
            footer: () => {}
        }
    ];

    return columns;
};

export const convertToPortalOptionPositionRow = (
    locale: string,
    optionPosition: FxOptionPositionData,
    dateFormatter: Intl.DateTimeFormat,
    cashEntryCurrencies?: Currency[]
): PortalOptionPositionRow => {
    const amount = optionPosition.amount;

    const amountFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    const priceFormatter = new Intl.NumberFormat(locale, convertedQtyFormatterConfig);

    const [base, quote] = optionPosition.instrumentName.split('/');
    let baseAssetName = base;
    let quoteAssetName = quote;

    const foundBase = cashEntryCurrencies?.find((currency) => currency.code === base);
    const foundQuote = cashEntryCurrencies?.find((currency) => currency.code === quote);

    if (foundBase) baseAssetName = foundBase.assetName || foundBase.code;
    if (foundQuote) quoteAssetName = foundQuote.assetName || foundQuote.code;

    const tableSymbol = `${baseAssetName}/${quoteAssetName}`;

    const premium = BigNumber(amount).multipliedBy(optionPosition.premiumPrice).negated().toNumber();

    return {
        ...optionPosition,
        tableType: optionPosition.type,
        tableSubType: optionPosition.subType || '-',
        tablePrice: priceFormatter.format(optionPosition.premiumPrice),
        tableStrikePrice: optionPosition.strikePrice ? priceFormatter.format(optionPosition.strikePrice) : '-',
        tableAmount: amountFormatter.format(Math.abs(amount)),
        tableLowerBarrier: optionPosition.lowerBarrier ? priceFormatter.format(optionPosition.lowerBarrier) : '-',
        tableUpperBarrier: optionPosition.upperBarrier ? priceFormatter.format(optionPosition.upperBarrier) : '-',
        tableSymbol,
        baseAssetName,
        quoteAssetName,
        tradeStatus: optionPosition.status,
        tableTradeDate: dateFormatter.format(new Date(optionPosition.tradeDate)),
        tableExpiryDate: dateFormatter.format(new Date(optionPosition.expiryDate)),
        tableCurrentPrice: priceFormatter.format(optionPosition.currentPrice),
        tableCurrentValue: priceFormatter.format(optionPosition.currentValue),
        tableCurrentValueInBaseCurrency: priceFormatter.format(optionPosition.currentValueInBaseCurrency),
        premium,
        amount
    };
};

const TooltipEntry = ({ label, value }: { label: string; value: any }) => {
    return value ? (
        <div className="flex gap-3 items-center text-sm text-neutral-200">
            <span className="w-24">{label}</span>
            <span className="">:</span>
            <span className="">{value}</span>
        </div>
    ) : null;
};
